import React from 'react';
import Header from '../components/Header';
import BreakingNews from '../components/BreakingNews';
import Footer from '../components/Footer';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Calendar, Award, Users, BookOpen, TrendingUp, Heart } from 'lucide-react';

const FiftyYears = () => {
  const milestones = [
    {
      year: '1974',
      yearBengali: '১৯৭৪',
      title: 'Foundation',
      titleBengali: 'প্রতিষ্ঠা',
      description: 'Tripura Darpan was founded with a vision to deliver independent and unbiased news to the people of Tripura',
      descriptionBengali: 'স্বাধীন এবং নিরপেক্ষ সংবাদ পরিবেশনের লক্ষ্যে ত্রিপুরা দর্পণ প্রতিষ্ঠিত হয়েছিল'
    },
    {
      year: '1980',
      yearBengali: '১৯৮০',
      title: 'Expansion',
      titleBengali: 'সম্প্রসারণ',
      description: 'Extended coverage to all districts of Tripura',
      descriptionBengali: 'ত্রিপুরার সমস্ত জেলায় কভারেজ সম্প্রসারিত করা হয়েছে'
    },
    {
      year: '1995',
      yearBengali: '১৯৯৫',
      title: 'Digital Era',
      titleBengali: 'ডিজিটাল যুগ',
      description: 'Embraced digital printing and modern technology',
      descriptionBengali: 'ডিজিটাল মুদ্রণ এবং আধুনিক প্রযুক্তি গ্রহণ করা হয়েছে'
    },
    {
      year: '2010',
      yearBengali: '২০১০',
      title: 'Online Portal',
      titleBengali: 'অনলাইন পোর্টাল',
      description: 'Launched online news portal reaching global audience',
      descriptionBengali: 'বৈশ্বিক দর্শকদের কাছে পৌঁছানোর জন্য অনলাইন সংবাদ পোর্টাল চালু করা হয়েছে'
    },
    {
      year: '2024',
      yearBengali: '২০২৪',
      title: 'Golden Jubilee',
      titleBengali: 'সুবর্ণ জয়ন্তী',
      description: 'Celebrating 50 glorious years of journalism excellence',
      descriptionBengali: 'সাংবাদিকতার শ্রেষ্ঠত্বের ৫০টি গৌরবময় বছর উদযাপন'
    }
  ];

  const achievements = [
    {
      icon: Users,
      number: '5M+',
      label: 'Readers',
      labelBengali: 'পাঠক'
    },
    {
      icon: BookOpen,
      number: '50K+',
      label: 'Stories Published',
      labelBengali: 'প্রকাশিত সংবাদ'
    },
    {
      icon: Award,
      number: '25+',
      label: 'Journalism Awards',
      labelBengali: 'সাংবাদিকতা পুরস্কার'
    },
    {
      icon: TrendingUp,
      number: '50',
      label: 'Years of Excellence',
      labelBengali: 'বছরের শ্রেষ্ঠত্ব'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <BreakingNews />

      {/* Hero Section with Anniversary Banner */}
      <div className="relative bg-gradient-to-br from-red-700 via-red-600 to-red-800 text-white py-16 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.4"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            backgroundSize: '30px 30px'
          }}></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="bg-yellow-400 text-red-900 px-4 py-2 text-lg font-bold mb-6">
              স্বর্ণ জয়ন্তী • Golden Jubilee
            </Badge>
            <h1 className="text-5xl md:text-7xl font-bold mb-4" style={{ fontFamily: 'serif' }}>
              ত্রিপুরা দর্পণ
            </h1>
            <div className="text-3xl md:text-4xl font-bold mb-6 tracking-wider">
              ১৯৭৪ - ২০২৪
            </div>
            <p className="text-2xl md:text-3xl mb-4 font-semibold">
              ৫০ বছর সেবার • 50 Years of Service
            </p>
            <p className="text-xl md:text-2xl text-red-100">
              স্বাধীন সংবাদ, নিরপেক্ষ ধারা
            </p>
            <p className="text-lg md:text-xl text-red-100 mt-2">
              Independent News, Unbiased Stream
            </p>
          </div>
        </div>
      </div>

      {/* 50 Years Logo Section */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <Card className="overflow-hidden shadow-2xl border-2 border-red-200">
            <CardContent className="p-0">
              <img
                src="https://customer-assets.emergentagent.com/job_state-mirror-1/artifacts/nnoaospk_WhatsApp%20Image%202026-03-15%20at%202.13.20%20PM.jpeg"
                alt="Tripura Darpan 50 Years Anniversary Logo"
                className="w-full h-auto"
              />
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Achievements Section */}
      <div className="bg-white py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-3">
              আমাদের অর্জন
            </h2>
            <p className="text-2xl text-gray-600">Our Achievements</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {achievements.map((achievement, index) => {
              const Icon = achievement.icon;
              return (
                <Card key={index} className="text-center border-red-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                  <CardContent className="pt-8 pb-6">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                      <Icon className="w-8 h-8 text-red-600" />
                    </div>
                    <div className="text-4xl font-bold text-red-600 mb-2">
                      {achievement.number}
                    </div>
                    <p className="text-gray-900 font-semibold mb-1">
                      {achievement.label}
                    </p>
                    <p className="text-gray-600 text-sm">
                      {achievement.labelBengali}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>

      {/* Journey Timeline */}
      <div className="container mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-3">
            আমাদের যাত্রা
          </h2>
          <p className="text-2xl text-gray-600">Our Journey Through Time</p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="relative">
            {/* Timeline Line */}
            <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-red-200"></div>

            {/* Timeline Items */}
            {milestones.map((milestone, index) => (
              <div key={index} className="relative mb-12 md:mb-16">
                <div className={`flex flex-col md:flex-row items-center ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                  {/* Content Card */}
                  <div className="w-full md:w-5/12">
                    <Card className="border-red-200 hover:shadow-xl transition-all duration-300">
                      <CardHeader>
                        <div className="flex items-center justify-between mb-2">
                          <Badge className="bg-red-600 text-white text-lg px-3 py-1">
                            {milestone.yearBengali}
                          </Badge>
                          <span className="text-gray-500 font-semibold">{milestone.year}</span>
                        </div>
                        <CardTitle className="text-xl">
                          {milestone.titleBengali}
                        </CardTitle>
                        <CardDescription className="text-base">
                          {milestone.title}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <p className="text-gray-700 mb-2">{milestone.descriptionBengali}</p>
                        <p className="text-gray-600 text-sm">{milestone.description}</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Timeline Dot */}
                  <div className="hidden md:flex w-2/12 justify-center">
                    <div className="w-6 h-6 bg-red-600 rounded-full border-4 border-white shadow-lg z-10"></div>
                  </div>

                  {/* Spacer */}
                  <div className="hidden md:block w-5/12"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Message Section */}
      <div className="bg-gradient-to-br from-red-50 to-white py-16">
        <div className="container mx-auto px-4">
          <Card className="max-w-4xl mx-auto border-red-200 shadow-xl">
            <CardHeader className="text-center pb-4">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4 mx-auto">
                <Heart className="w-8 h-8 text-red-600" />
              </div>
              <CardTitle className="text-3xl text-gray-900 mb-2">
                কৃতজ্ঞতা বার্তা
              </CardTitle>
              <CardDescription className="text-xl">
                Message of Gratitude
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center">
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                গত ৫০ বছর ধরে, ত্রিপুরা দর্পণ ত্রিপুরার মানুষের বিশ্বস্ত সংবাদপত্র হিসেবে কাজ করে আসছে। 
                আমরা আমাদের পাঠক, লেখক, সাংবাদিক এবং সমর্থকদের অসংখ্য ধন্যবাদ জানাই যারা এই যাত্রায় 
                আমাদের সাথে ছিলেন।
              </p>
              <p className="text-gray-600 leading-relaxed">
                For the past 50 years, Tripura Darpan has served as the trusted newspaper of the people of Tripura. 
                We extend our heartfelt gratitude to our readers, writers, journalists, and supporters who have been 
                with us on this journey.
              </p>
              <div className="mt-8 pt-6 border-t border-gray-200">
                <p className="text-xl font-semibold text-red-600 mb-2">
                  সত্যের প্রতি প্রতিশ্রুতিবদ্ধ
                </p>
                <p className="text-lg text-gray-600">
                  Committed to Truth
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default FiftyYears;